/**
 * Flexer Server - Rol Bazlı Yetki Sistemi
 *
 * Bu dosya artık PermissionContext'ten gelen API verilerini kullanır.
 * Hardcoded yetkiler sadece fallback olarak tutulur.
 *
 * Ana kaynak: GET /api/v1/roles/my-permissions
 */

// PermissionContext referansı - App.js'de set edilir
let _permissionContext = null;

export const setPermissionContext = (ctx) => {
  _permissionContext = ctx;
};

// Kullanıcı rolleri (backward compat)
export const USER_ROLES = {
  ADMIN: 'admin',
  MANAGER: 'manager',
  TECHNICIAN: 'technician',
  OPERATOR: 'operator',
  CUSTOMER: 'customer'
};

// Rol hiyerarşisi - fallback
const FALLBACK_ROLE_HIERARCHY = {
  [USER_ROLES.ADMIN]: 5,
  [USER_ROLES.MANAGER]: 4,
  [USER_ROLES.TECHNICIAN]: 3,
  [USER_ROLES.OPERATOR]: 2,
  [USER_ROLES.CUSTOMER]: 1
};

export const ROLE_HIERARCHY = FALLBACK_ROLE_HIERARCHY;

// Menü öğeleri (icon bilgileri frontend'de kalmalı)
export const MENU_ITEMS = {
  dashboard: {
    key: '/',
    icon: 'DashboardOutlined',
    label: 'Dashboard',
  },
  devices: {
    key: '/devices',
    icon: 'DesktopOutlined',
    label: 'Cihazlar',
  },
  users: {
    key: '/users',
    icon: 'UserOutlined',
    label: 'Kullanıcılar',
  },
  customers: {
    key: '/customers',
    icon: 'TeamOutlined',
    label: 'Müşteriler',
  },
  rentals: {
    key: '/rentals',
    icon: 'ShopOutlined',
    label: 'Kiralamalar',
  },
  billing: {
    key: '/billing',
    icon: 'DollarOutlined',
    label: 'Faturalandırma',
  },
  firmware: {
    key: '/firmware',
    icon: 'CloudUploadOutlined',
    label: 'Firmware',
  },
  telemetry: {
    key: '/telemetry',
    icon: 'LineChartOutlined',
    label: 'Telemetry',
  },
  deviceLogs: {
    key: '/logs/device',
    icon: 'FileTextOutlined',
    label: 'Device Logs',
  },
  applicationLogs: {
    key: '/logs/application',
    icon: 'FileTextOutlined',
    label: 'Application Logs',
  },
  mqttControl: {
    key: '/mqtt/control',
    icon: 'ApiOutlined',
    label: 'MQTT Control',
  },
  monitoring: {
    key: '/monitoring/dashboard',
    icon: 'MonitorOutlined',
    label: 'Monitoring',
  },
  settings: {
    key: '/settings',
    icon: 'SettingOutlined',
    label: 'Ayarlar',
  }
};

/**
 * Kullanıcının belirli bir role sahip olup olmadığını kontrol eder
 */
export const hasRole = (userRole, requiredRoles) => {
  if (!userRole) return false;

  // userRole obje ise (API'den gelen Role nesnesi) name alanını al
  const roleName = typeof userRole === 'object' ? userRole.name : userRole;
  const roles = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];
  return roles.includes(roleName);
};

/**
 * Kullanıcının belirli bir sayfaya erişim yetkisi olup olmadığını kontrol eder
 */
export const hasPageAccess = (userRole, path) => {
  if (!userRole) return false;

  // PermissionContext varsa API verisini kullan
  if (_permissionContext?.permissionsLoaded) {
    return _permissionContext.hasPageAccess(path);
  }

  // Fallback: role name check is not possible without hardcoded data
  // Herkesin erişebildiği sayfalar
  const publicPages = ['/', '/profile'];
  if (publicPages.includes(path)) return true;

  return false;
};

/**
 * Kullanıcının belirli bir API endpoint'ine erişim yetkisi olup olmadığını kontrol eder
 */
export const hasApiAccess = (userRole, method, endpoint) => {
  if (!userRole) return false;

  // PermissionContext'ten kontrol edilemez (API bazlı değil)
  // Bu fonksiyon artık kullanılmıyor
  return true;
};

/**
 * Kullanıcının rolüne göre erişebileceği menü öğelerini döndürür
 */
export const getAccessibleMenuItems = (userRole) => {
  if (!userRole) return [];

  if (_permissionContext?.permissionsLoaded) {
    const allowedKeys = _permissionContext.menuItems;
    return Object.values(MENU_ITEMS).filter(item =>
      allowedKeys.includes(item.key)
    );
  }

  // Fallback: dashboard only
  return [MENU_ITEMS.dashboard];
};

/**
 * Kullanıcının rol hiyerarşisindeki seviyesini döndürür
 */
export const getRoleLevel = (userRole) => {
  if (!userRole) return 0;

  if (_permissionContext?.permissionsLoaded && _permissionContext.roleInfo) {
    return _permissionContext.roleInfo.hierarchy_level || 0;
  }

  const roleName = typeof userRole === 'object' ? userRole.name : userRole;
  return FALLBACK_ROLE_HIERARCHY[roleName] || 0;
};

/**
 * Kullanıcının belirli bir rol seviyesine sahip olup olmadığını kontrol eder
 */
export const hasRoleLevel = (userRole, minLevel) => {
  const userLevel = getRoleLevel(userRole);
  return userLevel >= minLevel;
};

/**
 * Rol adını Türkçe olarak döndürür
 */
export const getRoleDisplayName = (role) => {
  if (!role) return '';

  // Role obje ise display_name'i kullan
  if (typeof role === 'object' && role.display_name) {
    return role.display_name;
  }

  const roleName = typeof role === 'object' ? role.name : role;
  const roleNames = {
    [USER_ROLES.ADMIN]: 'Yönetici',
    [USER_ROLES.MANAGER]: 'Müdür',
    [USER_ROLES.TECHNICIAN]: 'Teknisyen',
    [USER_ROLES.OPERATOR]: 'Operatör',
    [USER_ROLES.CUSTOMER]: 'Müşteri'
  };

  return roleNames[roleName] || roleName;
};

/**
 * Rol rengini döndürür (UI için)
 */
export const getRoleColor = (role) => {
  if (!role) return '#d9d9d9';

  // Role obje ise color alanını kullan
  if (typeof role === 'object' && role.color) {
    return role.color;
  }

  const roleName = typeof role === 'object' ? role.name : role;
  const roleColors = {
    [USER_ROLES.ADMIN]: '#ff4d4f',
    [USER_ROLES.MANAGER]: '#fa8c16',
    [USER_ROLES.TECHNICIAN]: '#1890ff',
    [USER_ROLES.OPERATOR]: '#52c41a',
    [USER_ROLES.CUSTOMER]: '#722ed1'
  };

  return roleColors[roleName] || '#d9d9d9';
};

/**
 * UI Element yetki kontrolü: hasPermission('devices', 'add_device')
 */
export const hasPermission = (userRole, permission) => {
  if (!userRole) return false;

  // PermissionContext varsa API verisini kullan
  if (_permissionContext?.permissionsLoaded) {
    const [category, action] = permission.split('.');
    return _permissionContext.hasPermission(category, action);
  }

  return false;
};

export const canAccess = (userRole, permissions) => {
  if (!userRole || !permissions) return false;

  if (Array.isArray(permissions)) {
    return permissions.some(permission => hasPermission(userRole, permission));
  }

  return hasPermission(userRole, permissions);
};

export const getVisibleMenuItems = (userRole) => {
  if (!userRole) return [];
  return getAccessibleMenuItems(userRole).map(item => ({ ...item }));
};

export const getActionButtons = (userRole, category) => {
  if (!userRole || !category) return [];

  const buttons = [];

  if (hasPermission(userRole, `${category}.add_${category.slice(0, -1)}`)) {
    buttons.push('add');
  }

  if (hasPermission(userRole, `${category}.export_${category.slice(0, -1)}`)) {
    buttons.push('export');
  }

  switch (category) {
    case 'devices':
      if (hasPermission(userRole, 'devices.send_commands')) buttons.push('command');
      if (hasPermission(userRole, 'devices.view_telemetry')) buttons.push('telemetry');
      break;
    case 'users':
      if (hasPermission(userRole, 'users.change_user_role')) buttons.push('change_role');
      break;
    case 'firmware':
      if (hasPermission(userRole, 'firmware.upload_firmware')) buttons.push('upload');
      if (hasPermission(userRole, 'firmware.deploy_firmware')) buttons.push('deploy');
      break;
    default:
      break;
  }

  return buttons;
};

export const getTableColumns = (userRole, category) => {
  if (!userRole || !category) return [];
  // Artık tüm kolonlar gösterilir, backend izinleri kontrol eder
  return ['id', 'name', 'created_at', 'actions'];
};

// Sabit tema
export const getRoleBasedTheme = (userRole) => {
  return {
    primaryColor: '#1890ff',
    backgroundColor: '#f0f2f5',
    headerColor: '#001529'
  };
};

// Hoş geldin mesajları
export const getRoleBasedMessages = (userRole) => {
  const roleName = typeof userRole === 'object' ? userRole.name : userRole;
  const displayName = getRoleDisplayName(userRole);

  return {
    welcome: `${displayName} olarak giriş yaptınız.`,
    restrictions: []
  };
};

// UI_PERMISSIONS artık API'den geliyor, bu obje sadece fallback/referans
export const UI_PERMISSIONS = {};

// PAGE_PERMISSIONS ve API_PERMISSIONS artık API'den geliyor
export const PAGE_PERMISSIONS = {};
export const API_PERMISSIONS = {};
